import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useGreetingName } from '@/hooks/useGreetingName'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { copy, siteCopy } from './Slide3Charity.copy'

const ease = [0.16, 1, 0.3, 1] as const

const STEP_DURATION = 1800
const STEP_DONE = 4
type Step = 0 | 1 | 2 | 3 | 4
type Section = 1 | 2 | 3

function spotlight(section: Section, step: Step) {
  if (step === 0 || step === STEP_DONE) return 1
  return step === section ? 1 : 0.25
}

function highlightBg(section: Section, step: Step) {
  return step === section
    ? 'rgba(254, 240, 138, 0.7)'
    : 'rgba(254, 240, 138, 0)'
}

export default function Slide3Charity({ onComplete }: SlideProps) {
  const c = useSlideCopy(copy, {
    site: siteCopy,
    skipSpellingFor: ['senderEmail', 'realUrl'],
  })
  const greetingName = useGreetingName()
  const firstName = greetingName !== 'Customer' ? greetingName : null
  const [step, setStep] = useState<Step>(0)

  const SECTIONS = [
    { key: 'trust', label: c.trustLabel, items: [c.trustItem1, c.trustItem2], num: 1 as Section },
    { key: 'emotion', label: c.emotionLabel, items: [c.pressureEmpathy, c.pressureGuilt, c.pressureUrgency], num: 2 as Section },
    { key: 'action', label: c.actionLabel, items: [c.actionItem], num: 3 as Section },
  ]

  const handleReveal = () => {
    if (step !== 0) return
    setStep(1)
    onComplete()
  }

  useEffect(() => {
    if (step === 0 || step >= STEP_DONE) return
    const t = setTimeout(() => setStep((s) => (s + 1) as Step), STEP_DURATION)
    return () => clearTimeout(t)
  }, [step])

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 max-w-4xl mx-auto">
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease }}
        className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-8 w-full text-start"
      >
        {c.title}
      </motion.h2>

      {/* Horizontal layout: message left, pattern right */}
      <div className="w-full grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-6 sm:gap-8 items-center">
        {/* Email */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease }}
          className="min-w-0"
        >
          <div className="rounded-xl border border-slate-200 bg-white text-start shadow-sm overflow-hidden">
            <motion.div
              initial={false}
              animate={{ opacity: spotlight(1, step), backgroundColor: highlightBg(1, step) }}
              transition={{ duration: 0.6, ease }}
              className="border-b border-slate-100 px-6 py-4 space-y-2"
            >
              <p className="text-base font-semibold text-slate-900 truncate">
                {c.emailSubject}
              </p>
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-amber-500 flex items-center justify-center text-xs font-bold text-white shrink-0">
                  {c.senderInitials}
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-slate-500 truncate">
                    {c.senderName} &lt;{c.senderEmail}&gt;
                  </p>
                  <p className="text-sm text-slate-400">{c.senderToLine}</p>
                </div>
              </div>
            </motion.div>
            <div className="px-6 py-5">
              <motion.p
                initial={false}
                animate={{ opacity: spotlight(2, step), backgroundColor: highlightBg(2, step) }}
                transition={{ duration: 0.6, ease }}
                className="text-base sm:text-lg text-slate-900 leading-relaxed rounded"
              >
                {firstName ? `${c.greeting.replace('{name}', firstName)}${c.bodyOpenLower}` : c.bodyOpenUpper}
              </motion.p>
              <p className="mt-3 text-base sm:text-lg text-slate-900 leading-relaxed">
                <motion.span
                  initial={false}
                  animate={{ opacity: spotlight(2, step), backgroundColor: highlightBg(2, step) }}
                  transition={{ duration: 0.6, ease }}
                  className="rounded [box-decoration-break:clone]"
                >
                  {c.bodyMatch}
                </motion.span>
                <motion.span
                  initial={false}
                  animate={{ opacity: spotlight(3, step), backgroundColor: highlightBg(3, step) }}
                  transition={{ duration: 0.6, ease }}
                  className="text-blue-600 underline cursor-pointer relative group rounded [box-decoration-break:clone]"
                >
                  {c.bodyCta}
                  <span className="pointer-events-none absolute start-0 -top-8 hidden group-hover:block text-xs text-slate-500 bg-slate-100 border border-slate-200 rounded px-2 py-1 whitespace-nowrap shadow-sm">
                    {c.realUrl}
                  </span>
                </motion.span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Arrow */}
        <motion.div
          animate={{ opacity: step >= 1 ? 1 : 0 }}
          transition={{ duration: 0.4, delay: step >= 1 ? 0.15 : 0 }}
          className="hidden sm:flex items-center justify-center"
        >
          <svg width="32" height="24" viewBox="0 0 32 24" fill="none" className="text-slate-300 dir-flip">
            <path d="M0 12h24m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.div>

        {/* Right side: button or pattern breakdown */}
        <div className="flex items-center min-w-0">
          {step === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7, ease }}
              className="w-full flex justify-center sm:justify-start"
            >
              <button
                onClick={handleReveal}
                className="rounded-full border border-slate-200 px-8 py-3.5 text-lg font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors cursor-pointer"
              >
                {c.revealButton}
              </button>
            </motion.div>
          ) : (
            <div className="text-start space-y-7 w-full">
              {SECTIONS.map((s) => {
                const isVisible = step >= s.num
                const isActive = step === s.num
                const targetOpacity = step === STEP_DONE ? 1 : isActive ? 1 : isVisible ? 0.5 : 0
                return (
                  <motion.div
                    key={s.key}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: targetOpacity, x: isVisible ? 0 : 12 }}
                    transition={{ duration: 0.6, ease }}
                  >
                    <p className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
                      {s.num}. {s.label}
                    </p>
                    <ul className="mt-0.5 space-y-0.5">
                      {s.items.map((item, i) => (
                        <li key={i} className="text-base sm:text-lg text-slate-500 leading-snug">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
